/*
  The Kashira — collection motion & states (spec 02 Collection).

  Owns four behaviours that sit on top of Dawn's facets.js pipeline:
    1. Filter drawer close gestures — swipe right (touch, threshold based)
       and a tap on the scrim.
    2. Pagination page-change smooth scroll to grid top (~400ms).
    3. Skeleton shimmer for Section-Rendering responses slower than 400ms.
    4. Post-filter card re-entry stagger + live apply count (150ms swap).

  facets.js calls into window.KashiraGrid at the three render checkpoints.
  Every animation here is gated on prefers-reduced-motion.
*/
(function () {
  'use strict';

  const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const prefersReducedMotion = () => reduceMotionQuery.matches;

  /* ----------------------------------------------------------------------
     Shared grid helpers used by facets.js (skeleton, stagger, apply count)
     ---------------------------------------------------------------------- */

  const SKELETON_DELAY = 400; // spec 02: never flash on fast responses
  const STAGGER_STEP = 30; // ms between card re-entries
  const STAGGER_CAP = 24; // stop compounding delay past the first rows
  const COUNT_SWAP = 150; // ms number crossfade on the apply button

  let skeletonTimer = null;

  function gridContainer() {
    return document.getElementById('ProductGridContainer');
  }

  const KashiraGrid = {
    /* Arm the shimmer. Only paints if the fetch is still in flight at 400ms. */
    startPending() {
      const container = gridContainer();
      if (!container) return;
      window.clearTimeout(skeletonTimer);
      skeletonTimer = window.setTimeout(() => {
        // Reserve the current grid height so the swap causes zero layout shift.
        const grid = container.querySelector('.product-grid');
        if (grid) container.style.setProperty('--kashira-skeleton-height', `${grid.offsetHeight}px`);
        container.classList.add('kashira-grid--pending');
      }, SKELETON_DELAY);
    },

    stopPending() {
      window.clearTimeout(skeletonTimer);
      skeletonTimer = null;
      const container = gridContainer();
      if (!container) return;
      container.classList.remove('kashira-grid--pending');
      container.style.removeProperty('--kashira-skeleton-height');
    },

    /* Re-enter the freshly rendered cards: fade + rise 300ms, 30ms stagger.
       facets.js cancels Dawn's scroll-reveal on these nodes, so this is the
       only reveal that runs. Reduced motion: leave them at their final state. */
    staggerCards() {
      const container = gridContainer();
      if (!container) return;
      const cards = container.querySelectorAll('.product-grid > .grid__item');
      if (!cards.length || prefersReducedMotion()) return;

      cards.forEach((card, index) => {
        card.style.setProperty('--kashira-enter-order', String(Math.min(index, STAGGER_CAP)));
        card.classList.remove('kashira-card-enter');
        // Force a reflow so a re-applied class restarts the animation.
        void card.offsetWidth;
        card.classList.add('kashira-card-enter');
        card.addEventListener(
          'animationend',
          () => {
            card.classList.remove('kashira-card-enter');
            card.style.removeProperty('--kashira-enter-order');
          },
          { once: true }
        );
      });
    },

    /* Live result count on the mobile facets footer CTA. */
    updateApplyCount() {
      const source = document.getElementById('ProductCount');
      const targets = document.querySelectorAll('[data-kashira-apply-count]');
      if (!source || !targets.length) return;

      const count = parseInt(source.dataset.productCount, 10);
      if (Number.isNaN(count)) return;

      targets.forEach((target) => {
        const template =
          count === 1 && target.dataset.kashiraApplyCountOne
            ? target.dataset.kashiraApplyCountOne
            : target.dataset.kashiraApplyCount;
        const next = template ? template.replace('[count]', String(count)) : String(count);
        if (target.textContent.trim() === next) return;

        if (prefersReducedMotion()) {
          target.textContent = next;
          return;
        }

        target.classList.add('is-swapping');
        window.setTimeout(() => {
          target.textContent = next;
        }, COUNT_SWAP / 2);
        window.setTimeout(() => {
          target.classList.remove('is-swapping');
        }, COUNT_SWAP);
      });
    },
  };

  window.KashiraGrid = KashiraGrid;

  /* ----------------------------------------------------------------------
     2. Pagination — smooth scroll to grid top after a page change
     ---------------------------------------------------------------------- */

  function scrollToGridTop() {
    const container = gridContainer();
    if (!container) return;
    const header = document.querySelector('.section-header');
    const offset = (header ? header.offsetHeight : 0) + 24;
    const target = Math.max(0, window.scrollY + container.getBoundingClientRect().top - offset);

    if (prefersReducedMotion()) {
      window.scrollTo(0, target);
      return;
    }

    // Hand-rolled 400ms tween so the duration matches the spec regardless of
    // the browser's native smooth-scroll timing.
    const start = window.scrollY;
    const delta = target - start;
    if (Math.abs(delta) < 2) return;
    const duration = 400;
    const startedAt = performance.now();
    // Mirrors --kashira-ease cubic-bezier(0.22, 0.61, 0.36, 1) closely enough
    // for a scroll tween (ease-out cubic).
    const ease = (t) => 1 - Math.pow(1 - t, 3);

    const step = (now) => {
      const t = Math.min(1, (now - startedAt) / duration);
      window.scrollTo(0, start + delta * ease(t));
      if (t < 1) window.requestAnimationFrame(step);
    };
    window.requestAnimationFrame(step);
  }

  function initPaginationScroll() {
    const wrapper = document.querySelector('.pagination-wrapper');
    if (!wrapper) return;

    const currentPage = parseInt(new URLSearchParams(window.location.search).get('page'), 10);
    if (currentPage > 1) {
      // Landed on a paginated page: bring the grid top into view.
      window.requestAnimationFrame(scrollToGridTop);
    }
  }

  /* ----------------------------------------------------------------------
     1. Filter drawer — swipe right to close (touch only)
     ---------------------------------------------------------------------- */

  const SWIPE_DISTANCE = 60; // px of horizontal travel before we close
  const SWIPE_SLOP = 20; // px of vertical drift that means "this is a scroll"

  function initDrawerSwipe() {
    const panel = document.querySelector('.mobile-facets__inner');
    if (!panel || panel.dataset.kashiraSwipe === 'true') return;
    panel.dataset.kashiraSwipe = 'true';

    let swipe = null;

    // Passive listeners throughout: this handler never calls preventDefault,
    // so vertical scrolling inside the drawer and taps on checkboxes,
    // the close button and the scrim all behave exactly as before.
    panel.addEventListener(
      'touchstart',
      (event) => {
        if (event.touches.length !== 1) {
          swipe = null;
          return;
        }
        const touch = event.touches[0];
        swipe = { x: touch.clientX, y: touch.clientY, aborted: false };
      },
      { passive: true }
    );

    panel.addEventListener(
      'touchmove',
      (event) => {
        if (!swipe || swipe.aborted || event.touches.length !== 1) return;
        const touch = event.touches[0];
        const dx = touch.clientX - swipe.x;
        const dy = touch.clientY - swipe.y;
        // Vertical intent wins — let the drawer scroll.
        if (Math.abs(dy) > SWIPE_SLOP && Math.abs(dy) > Math.abs(dx)) swipe.aborted = true;
      },
      { passive: true }
    );

    panel.addEventListener(
      'touchend',
      (event) => {
        const current = swipe;
        swipe = null;
        if (!current || current.aborted || !event.changedTouches.length) return;
        const dx = event.changedTouches[0].clientX - current.x;
        const dy = event.changedTouches[0].clientY - current.y;
        if (dx < SWIPE_DISTANCE || Math.abs(dy) > Math.abs(dx)) return;
        closeFacetsDrawer(panel);
      },
      { passive: true }
    );
  }

  /* Reuse the existing close path (the summary toggle) so menu-drawer's own
     focus/scroll-lock teardown runs — we never hand-roll the close. */
  function closeFacetsDrawer(panel) {
    const wrapper = panel.closest('.mobile-facets__wrapper');
    const summary = wrapper && wrapper.querySelector('summary');
    if (summary) summary.click();
  }

  /* Tap the 45% dim to close (spec 02 "Close: ✕ / scrim / ESC / swipe-right").
     The scrim is the .mobile-facets form itself and the panel is its child, so
     only clicks that land outside .mobile-facets__inner count. */
  function initDrawerScrim() {
    document.querySelectorAll('.mobile-facets').forEach((scrim) => {
      if (scrim.dataset.kashiraScrim === 'true') return;
      scrim.dataset.kashiraScrim = 'true';

      scrim.addEventListener('click', (event) => {
        if (event.target.closest('.mobile-facets__inner')) return;
        const panel = scrim.querySelector('.mobile-facets__inner');
        if (panel) closeFacetsDrawer(panel);
      });
    });
  }

  /* ----------------------------------------------------------------------
     Drawer chrome — open below the sticky header + robust body scroll-lock

     Dawn's menu-drawer only locks the body at the `mobile` breakpoint
     (overflow-hidden-mobile reverts to overflow:auto ≥750px), so the
     collection filter drawer — which opens at every width — left the page
     scrollable behind it on tablet/desktop. We lock <html> ourselves for
     all widths, compensate for the scrollbar so nothing shifts, and pin the
     panel to start at the sticky header's current bottom edge so the first
     facet is never clipped.
     ---------------------------------------------------------------------- */

  function lockScroll() {
    const root = document.documentElement;
    if (root.classList.contains('kashira-drawer-locked')) return;
    // overflow:hidden on <html> keeps scrollTop, so the header stays put and
    // scroll position is restored for free on unlock. Pad for the vanished
    // scrollbar so the page (and full-bleed header) does not jump.
    const scrollbar = window.innerWidth - root.clientWidth;
    root.style.setProperty('--kashira-scrollbar-pad', `${Math.max(0, scrollbar)}px`);
    root.classList.add('kashira-drawer-locked');
  }

  function unlockScroll() {
    const root = document.documentElement;
    root.classList.remove('kashira-drawer-locked');
    root.style.removeProperty('--kashira-scrollbar-pad');
  }

  function initDrawerChrome() {
    const wrapper = document.querySelector('.facets-container-drawer .mobile-facets__wrapper');
    if (!wrapper || wrapper.dataset.kashiraChrome === 'true') return;
    const details = wrapper.querySelector('details');
    if (!details) return;
    wrapper.dataset.kashiraChrome = 'true';

    const sync = () => (details.hasAttribute('open') ? lockScroll() : unlockScroll());
    new MutationObserver(sync).observe(details, {
      attributes: true,
      attributeFilter: ['open'],
    });
    sync();
  }

  /* Design 02 ships the drawer facet groups expanded, and spec 02 adds "open
     state persists per session". An AJAX facet re-render replaces the drawer
     markup wholesale, so the group a visitor collapsed comes back <details open>
     unless we remember it: the closed set is kept in sessionStorage under each
     group's stable id (it carries the filter's param_name) and re-applied after
     every render. (The availability toggle is a plain div without .js-filter,
     so it's left alone.) */
  const FACET_STATE_KEY = 'kashira:facets-closed';
  const FACET_SELECTOR = '.facets-container-drawer .mobile-facets__details.js-filter';

  function readClosedFacets() {
    try {
      const raw = sessionStorage.getItem(FACET_STATE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return []; // private mode / disabled storage — groups just default open
    }
  }

  function writeClosedFacets(closed) {
    try {
      sessionStorage.setItem(FACET_STATE_KEY, JSON.stringify(closed));
    } catch {
      /* nothing to persist to; the live DOM state still stands */
    }
  }

  function facetKey(details) {
    return details.id || details.dataset.index || '';
  }

  function restoreDrawerFacets() {
    const closed = readClosedFacets();
    document.querySelectorAll(FACET_SELECTOR).forEach((details) => {
      const key = facetKey(details);
      const shouldBeOpen = !closed.includes(key);
      if (shouldBeOpen && !details.hasAttribute('open')) details.setAttribute('open', '');
      if (!shouldBeOpen && details.hasAttribute('open')) details.removeAttribute('open');

      if (!key || details.dataset.kashiraFacetState === 'true') return;
      details.dataset.kashiraFacetState = 'true';
      details.addEventListener('toggle', () => {
        const next = readClosedFacets().filter((entry) => entry !== key);
        if (!details.hasAttribute('open')) next.push(key);
        writeClosedFacets(next);
      });
    });
  }

  /* ---------------------------------------------------------------------- */

  function init() {
    initDrawerSwipe();
    initDrawerScrim();
    initDrawerChrome();
    restoreDrawerFacets();
    initPaginationScroll();
    KashiraGrid.updateApplyCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The facets drawer markup is replaced wholesale after an AJAX facet update.
  document.addEventListener('kashira:facets-rendered', () => {
    initDrawerSwipe();
    initDrawerScrim();
    initDrawerChrome();
    restoreDrawerFacets();
  });
})();
